import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For quality care/results
import HealingIcon from '@mui/icons-material/Healing'; // For repair/recovery
import ReportProblemIcon from '@mui/icons-material/ReportProblem'; // For emergency nature
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled'; // For immediate call
import BuildIcon from '@mui/icons-material/Build'; // For repair options
import ShieldIcon from '@mui/icons-material/Shield'; // For prevention
import SpeedIcon from '@mui/icons-material/Speed'; // For prompt care
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const EmergencyChippedToothPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const immediateSteps = [
    { text: 'Call Pacifica Dental Immediately: Dial (408) 869-0998 for prompt advice and to schedule an urgent appointment.', icon: <PhoneEnabledIcon sx={{ color: 'primary.main' }} /> },
    { text: 'Rinse Your Mouth: Gently rinse with warm salt water to clean the area.', icon: <HealingIcon /> },
    { text: 'Save the Chip (If Possible): If you find the broken piece, keep it moist by placing it in a small container with milk or your saliva and bring it with you.', icon: <StarIcon /> }, // Using Star as "valuable piece"
    { text: 'Apply a Cold Compress: Use a cold pack on your cheek or lips over the chipped tooth area to reduce swelling and pain.', icon: <SpaIcon /> },
    { text: 'Manage Pain: If you are in pain, you can take over-the-counter pain relievers as directed, but avoid placing aspirin directly on the gums.', icon: <HealingIcon /> },
  ];

  const repairProcess = [
    {
      title: '1. Urgent Consultation & Swift Examination',
      description: 'When you arrive at Pacifica Dental, we\'ll prioritize your situation. Our dentists will quickly and gently examine the chipped tooth, assess the extent of the damage, and may take an X-ray to check for underlying issues.',
      icon: <SpeedIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '2. Discussing Your Best Treatment Options',
      description: 'Based on the chip\'s severity, location, and your overall dental health, we will clearly explain the most suitable repair options, considering aesthetics, function, and durability.',
      icon: <CheckCircleOutlineIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '3. Performing Your Tooth Repair Promptly',
      description: 'Once a treatment plan is agreed upon, we will proceed with the repair. Our goal is to alleviate any discomfort, restore your tooth\'s appearance and function, and prevent further complications as efficiently as possible.',
      icon: <BuildIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '4. Aftercare Guidance & Future Prevention',
      description: 'After your treatment, we\'ll provide specific instructions for caring for your repaired tooth and advice on how to minimize the risk of future dental injuries.',
      icon: <ShieldIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    }
  ];

  const repairOptions = [
    { name: 'Dental Bonding', desc: 'For minor chips, tooth-colored composite resin can be sculpted to perfectly restore the tooth\'s shape.', icon: <BuildIcon /> },
    { name: 'Porcelain Veneers', desc: 'For more visible chips on front teeth, a custom-made thin porcelain shell can provide a durable and highly aesthetic repair.', icon: <StarIcon /> },
    { name: 'Dental Crowns', desc: 'If a large piece of the tooth is broken or the tooth structure is significantly weakened, a crown (cap) may be needed to cover and protect the entire tooth.', icon: <HealingIcon /> },
    { name: 'Root Canal Therapy', desc: 'If the chip is deep and has exposed or damaged the tooth\'s pulp (nerve), a root canal may be necessary before placing a crown.', icon: <ReportProblemIcon /> },
  ];

  const whyChip = [
    'Accidents or trauma (e.g., a fall, a blow to the face, sports injuries).',
    'Biting down on hard foods (e.g., ice, hard candy, nuts).',
    'Using teeth as tools (e.g., to open packages).',
    'Weakened tooth structure due to large fillings or decay.',
    'Teeth grinding or clenching (bruxism).'
  ];
  
  const faqs = [
    {
      question: 'Is a chipped tooth a dental emergency?',
      answer: 'Yes, even if it doesn\'t hurt, a chipped tooth should be treated as a dental emergency. Prompt attention at Pacifica Dental can prevent further damage, infection, or increased sensitivity. Call us at (408) 869-0998.'
    },
    {
      question: 'What if my chipped tooth doesn\'t hurt?',
      answer: 'Even without pain, the tooth may have sharp edges that can cut your tongue or cheek, or the chip could expose inner layers of the tooth, making it vulnerable. It’s always best to have it checked quickly.'
    },
    {
      question: 'How quickly can I be seen at Pacifica Dental for a chipped tooth?',
      answer: 'We prioritize dental emergencies and will make every effort to see you as soon as possible, often on the same day. Please call us immediately at (408) 869-0998.'
    },
    {
        question: 'Can the saved piece of my tooth be reattached?',
        answer: 'In some cases, particularly with larger chips, it might be possible to reattach the fragment. Bring it with you to your appointment at Pacifica Dental. However, other repair methods like bonding are often more predictable.'
    },
    {
        question: 'How much will it cost to repair my chipped tooth?',
        answer: 'The cost depends on the severity of the chip and the type of treatment required (e.g., bonding, veneer, crown). We will discuss all options and associated costs with you before proceeding with treatment.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Emergency Chipped Tooth Repair | Pacifica Dental | (408) 869-0998</title>
        <meta name="description" content="Chipped a tooth? Pacifica Dental offers prompt emergency repair for broken or chipped teeth. Call us immediately at (408) 869-0998 for urgent care." />
        <meta name="keywords" content="emergency chipped tooth Pacifica Dental, broken tooth repair, cracked tooth Pacifica Dental, dental emergency (408) 869-0998, San Jose CA emergency dentist, Bay Area chipped tooth" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.light' // Emphasizing emergency
              }}
            >
              Chipped Tooth? Pacifica Dental is Here to Help!
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Prompt, Compassionate Care for Your Dental Emergency. Call <a href="tel:4088690998" style={{color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 'bold'}}>(408) 869-0998</a> Now.
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              A chipped or broken tooth can be a distressing experience, causing pain, sensitivity, or aesthetic concerns. At Pacifica Dental, we understand the urgency and offer prompt, effective treatment to restore your tooth and your comfort. Don't wait – immediate attention can prevent further damage and lead to the best possible outcome.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Immediate Steps Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Chipped Tooth? What to Do Right Now!
              </Typography>
              <Paper elevation={3} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark', }}> {/* Light red background */}
                <List>
                    {immediateSteps.map((step, index) => (
                        <ListItem key={index} sx={{py:1}}>
                            <ListItemIcon sx={{minWidth: 'auto', marginRight: 2}}>
                                {React.cloneElement(step.icon, { sx: { fontSize: '2rem', ...step.icon.props.sx }})}
                            </ListItemIcon>
                            <ListItemText primary={step.text} primaryTypographyProps={{fontSize: '1.1rem', fontWeight: index === 0 ? 'bold': 'normal'}}/>
                        </ListItem>
                    ))}
                </List>
              </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} /> 

            {/* How Pacifica Dental Addresses Your Chipped Tooth */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                 <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Our Emergency Chipped Tooth Process
                  </Typography>
                  {repairProcess.map((step, index) => (
                    <Box key={index} sx={{ mb: index === repairProcess.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                         {step.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>

              {/* Common Repair Options Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Repair Options for Chipped Teeth
                  </Typography>
                  {repairOptions.map((option, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                        <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                             {React.cloneElement(option.icon, { sx: { color: 'white', mr: 1, fontSize: '1.3rem' }})}
                            <Typography variant="h6" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                                {option.name}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                            {option.desc}
                        </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Why Did My Tooth Chip? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Common Causes of Chipped Teeth
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark' }}>
                <Grid container spacing={1}>
                    {whyChip.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <ReportProblemIcon fontSize="small"/>
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* Preventing Future Chips Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Protecting Your Smile: Preventing Future Chips
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark', }}>
                    <List>
                        {[
                            'Wear a custom-fitted mouthguard during sports or physical activities.',
                            'If you grind your teeth (bruxism), ask us at Pacifica Dental about a nightguard.',
                            'Avoid biting down on very hard foods like ice, hard candies, or popcorn kernels.',
                            'Never use your teeth as tools to open packages or bottles.',
                            'Maintain good oral hygiene and regular dental check-ups to keep teeth strong.'
                        ].map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <ShieldIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Chipped Tooth FAQs at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark', borderRadius:2 }}> {/* Emergency-themed background */}
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.dark', mb:3 }}>
                Why Choose Pacifica Dental for Your Chipped Tooth Emergency?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Prompt Emergency Appointments", text: "We prioritize dental emergencies and strive to see you the same day to alleviate pain and address your chipped tooth quickly.", icon: <SpeedIcon color="primary" /> },
                      { title: "Experienced Restorative Dentists", text: "Our skilled team at Pacifica Dental has extensive experience in diagnosing and repairing all types of tooth chips with precision.", icon: <BuildIcon color="primary" /> },
                      { title: "Comprehensive Treatment Options", text: "From simple bonding to crowns or veneers, we offer a full range of solutions to restore your tooth effectively and aesthetically.", icon: <StarIcon color="primary" /> },
                      { title: "Compassionate & Gentle Care", text: "We understand dental emergencies can be stressful. We provide a calming environment and gentle care to make your experience as comfortable as possible.", icon: <SpaIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} gutterBottom sx={{fontWeight: 700}}> {/* Larger, bolder */}
                    Chipped Tooth Emergency?
                </Typography>
                 <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600, mb:2}}>
                    Call Pacifica Dental Now!
                </Typography>
                <Typography variant="h4" sx={{mb:3, fontWeight: 'bold'}}> {/* Make phone number prominent */}
                    <Link to="tel:4088690998" style={{color: 'white', textDecoration: 'none'}}>(408) 869-0998</Link>
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9, fontSize: '1.1rem'}}>
                    Our dedicated team is ready to provide the urgent care you need.
                </Typography>
              <Button
                href="tel:4088690998" // Direct call for emergency
                variant="contained"
                color="secondary" // Or another contrasting color like 'light' if theme supports
                size="large"
                startIcon={<PhoneEnabledIcon />}
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'uppercase', // Emphasize action
                  fontWeight: 700, // Bolder
                  fontSize: '1.2rem', // Larger
                  backgroundColor: 'white', // Example: ensure high contrast
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: '#f0f0f0', // Lighter hover for white button
                  }
                }}
              >
                Call Us Immediately
              </Button>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default EmergencyChippedToothPacificaDental;